import { useState, useEffect } from 'react'
import { useLibraryStore } from '@/stores/library-store'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { FolderSelectTree } from '@/components/FolderSelectTree'
import { Loader2 } from 'lucide-react'

interface MoveDocumentsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  documentIds: string[]
}

export function MoveDocumentsDialog({
  open,
  onOpenChange,
  documentIds,
}: MoveDocumentsDialogProps) {
  const { documents, moveDocument, bulkMoveDocuments } = useLibraryStore()
  const [targetFolderId, setTargetFolderId] = useState<string | null>(null)
  const [isMoving, setIsMoving] = useState(false)

  // Determine current folder (for single doc) or undefined (for bulk/mixed)
  const currentFolderId =
    documentIds.length === 1
      ? documents.find((d) => d.id === documentIds[0])?.folderId ?? null
      : undefined

  const documentName =
    documentIds.length === 1
      ? documents.find((d) => d.id === documentIds[0])?.name
      : undefined

  // Reset selection when dialog opens
  useEffect(() => {
    if (open) {
      if (documentIds.length === 1) {
        setTargetFolderId(currentFolderId ?? null)
      } else {
        setTargetFolderId(null)
      }
    }
  }, [open, documentIds, currentFolderId])

  const handleMove = async () => {
    if (!targetFolderId) return
    setIsMoving(true)
    try {
      if (documentIds.length === 1 && documentIds[0]) {
        await moveDocument(documentIds[0], targetFolderId)
      } else {
        await bulkMoveDocuments(documentIds, targetFolderId)
      }
      onOpenChange(false)
    } catch {
      // Error handled in store
    } finally {
      setIsMoving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {documentIds.length === 1
              ? `Move "${documentName}"`
              : `Move ${documentIds.length} Documents`}
          </DialogTitle>
          <DialogDescription>
            Select a destination folder for the document
            {documentIds.length > 1 ? 's' : ''}.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <Label className="mb-2 block">Destination</Label>
          <FolderSelectTree
            selectedFolderId={targetFolderId}
            onSelect={(id) => setTargetFolderId(id)}
            currentFolderId={currentFolderId}
          />
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isMoving}
          >
            Cancel
          </Button>
          <Button onClick={() => void handleMove()} disabled={isMoving || targetFolderId === null}>
            {isMoving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Move
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
